import { isRecord, request, requireString } from '@/lib/api'
import type { components } from '@/lib/openapi.generated'
import type { Replace } from '@/lib/type-utils'

type Schemas = components['schemas']

export type AccessGrant = Replace<Schemas['AccessGrantDto'], 'subjectKind', {
  subjectKind: 'user' | 'machine' | 'group'
}>

export type InheritedAccessGrant = Replace<Schemas['InheritedAccessGrantDto'], 'subjectKind', {
  subjectKind: 'user' | 'machine' | 'group'
}>

export type AccessSummary = Replace<Schemas['AccessSummaryResponse'], 'assignedHere' | 'inherited', {
  assignedHere: AccessGrant[]
  inherited: InheritedAccessGrant[]
}>

function decodeSubjectKind(raw: unknown): 'user' | 'machine' | 'group' {
  const kind = requireString(raw, 'subjectKind')
  if (kind !== 'user' && kind !== 'machine' && kind !== 'group')
    throw new Error(`Unexpected subjectKind "${kind}"`)
  return kind
}

function decodeAccessGrant(raw: unknown): AccessGrant {
  if (!isRecord(raw)) throw new Error('Malformed access grant')
  return {
    subjectKind: decodeSubjectKind(raw.subjectKind),
    subjectId: requireString(raw.subjectId, 'subjectId'),
    subjectName: requireString(raw.subjectName, 'subjectName'),
    roleId: requireString(raw.roleId, 'roleId'),
    roleName: requireString(raw.roleName, 'roleName'),
  } satisfies AccessGrant
}

function decodeInheritedAccessGrant(raw: unknown): InheritedAccessGrant {
  if (!isRecord(raw)) throw new Error('Malformed inherited access grant')
  return {
    subjectKind: decodeSubjectKind(raw.subjectKind),
    subjectId: requireString(raw.subjectId, 'subjectId'),
    subjectName: requireString(raw.subjectName, 'subjectName'),
    roleId: requireString(raw.roleId, 'roleId'),
    roleName: requireString(raw.roleName, 'roleName'),
    sourceEntityId: requireString(raw.sourceEntityId, 'sourceEntityId'),
    sourceEntityName: requireString(raw.sourceEntityName, 'sourceEntityName'),
  } satisfies InheritedAccessGrant
}

function decodeAccessSummary(raw: unknown): AccessSummary {
  if (
    !isRecord(raw) ||
    !Array.isArray(raw.assignedHere) ||
    !Array.isArray(raw.inherited)
  )
    throw new Error('Malformed access summary response')
  return {
    assignedHere: raw.assignedHere.map(decodeAccessGrant),
    inherited: raw.inherited.map(decodeInheritedAccessGrant),
  } satisfies AccessSummary
}

export const accessApi = {
  getAccessSummary: (entityId: string) =>
    request(`/api/v1/entities/${entityId}/access-summary`, decodeAccessSummary),
}
